import axios from 'axios'

import parseErrors from '@/helpers/parse-errors'

/**
 * Shared axios instance for all app-web API calls.
 *
 * Centralizes the base URL, credentials, the 401 redirect-to-login flow,
 * and error parsing so individual services no longer repeat this boilerplate.
 *
 * The router/store modules are imported lazily (only when a 401 actually
 * needs handling) so that importing this module — and therefore any
 * service — doesn't statically pull in vue-router. Most tests mock
 * services or this module entirely and never exercise this path; the ones
 * that don't shouldn't be forced to also stub out the router.
 */
export const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_INTERNAL,
    withCredentials: true,
})

// Every request carries an X-Request-Id. The API stamps it on the changes the
// request makes, so this tab can tell its own broadcasts from other editors'.
const OWN_REQUEST_ID_LIMIT = 200
const ownRequestIds = new Set()

apiClient.interceptors.request.use((config) => {
    const requestId = globalThis.crypto.randomUUID()

    config.headers['X-Request-Id'] = requestId
    ownRequestIds.add(requestId)

    if (ownRequestIds.size > OWN_REQUEST_ID_LIMIT) {
        ownRequestIds.delete(ownRequestIds.values().next().value)
    }

    return config
})

/**
 * True when `requestId` belongs to a request this tab sent, so a broadcast
 * with that ID is an echo of this tab's own change.
 *
 * @param {string|null|undefined} requestId
 * @returns {boolean}
 */
export function isOwnRequestId(requestId) {
    return Boolean(requestId) && ownRequestIds.has(requestId)
}

let unauthorizedRedirectPromise = null

function isProtectedRoute(router) {
    const matchedRoutes = router.currentRoute?.value?.matched

    if (!Array.isArray(matchedRoutes)) {
        return false
    }

    return matchedRoutes.some((record) => record.meta.requiresSession)
}

function handleUnauthorizedResponse() {
    if (unauthorizedRedirectPromise) {
        return unauthorizedRedirectPromise
    }

    // Assigned synchronously (before the first await below runs) so that a
    // second concurrent 401 sees this promise already set instead of also
    // proceeding to log out and redirect.
    unauthorizedRedirectPromise = (async () => {
        const [
            { default: router },
            { default: pinia },
            { useAuthStore },
            { buildExpiredSessionLoginRedirect },
        ] = await Promise.all([
            import('@/router'),
            import('@/stores'),
            import('@/stores/auth'),
            import('@/helpers/expired-session-redirect'),
        ])

        const authStore = useAuthStore(pinia)
        authStore.logout(false)

        if (router.currentRoute.value.name === 'LoginScreen') {
            return
        }

        await router
            .push(buildExpiredSessionLoginRedirect(router.currentRoute.value))
            .catch(() => {
                // Ignore navigation duplication failures.
            })
    })().finally(() => {
        unauthorizedRedirectPromise = null
    })

    return unauthorizedRedirectPromise
}

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            try {
                const { default: router } = await import('@/router')

                if (isProtectedRoute(router)) {
                    handleUnauthorizedResponse().catch(() => {
                        // The dynamic imports above (or inside
                        // handleUnauthorizedResponse) can reject if a new
                        // deploy shipped since this page loaded and the old
                        // hashed chunk no longer exists. Fall back to a hard
                        // navigation so the user still reaches the login
                        // screen instead of getting stuck with an unhandled
                        // rejection.
                        window.location.assign('/login?t=1')
                    })
                }
            } catch {
                // The router chunk itself failed to load (e.g. a stale
                // hashed chunk after a deploy) — we can't tell whether the
                // current route is protected, so fall back to a hard
                // redirect rather than leaving the user stuck on a broken
                // page.
                window.location.assign('/login?t=1')
            }
        }

        return Promise.reject(parseErrors(error))
    }
)

export default apiClient
